import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { 
  fetchBusinessSettingsForTenant, 
  upsertBusinessSettingsForTenant 
} from '../../services/pocketbase';
import { DAYS_OF_WEEK } from '../../config/constants';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiMapPin, FiMail, FiPhone, FiClock, FiSettings } = FiIcons;

const BusinessSettings = () => {
  const { getEffectiveTenantId } = useAuth();
  const [settings, setSettings] = useState({
    address: '',
    email: '',
    phone: '',
    opening_hours: {}
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, [getEffectiveTenantId()]);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const tenantId = getEffectiveTenantId();
      if (tenantId) {
        const data = await fetchBusinessSettingsForTenant(tenantId);
        if (data) {
          setSettings({
            address: data.address || '',
            email: data.email || '',
            phone: data.phone || '',
            opening_hours: data.opening_hours ? JSON.parse(data.opening_hours) : getDefaultOpeningHours()
          });
        } else {
          setSettings({
            address: '',
            email: '',
            phone: '',
            opening_hours: getDefaultOpeningHours()
          });
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultOpeningHours = () => {
    const defaultHours = {};
    DAYS_OF_WEEK.forEach(day => {
      defaultHours[day] = {
        isOpen: day !== 'Saturday' && day !== 'Sunday',
        openTime: '09:00',
        closeTime: '17:00'
      };
    });
    return defaultHours;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (settings.email && !emailRegex.test(settings.email)) {
        throw new Error('Please enter a valid email address');
      }

      const tenantId = getEffectiveTenantId();
      const dataToSave = {
        ...settings,
        opening_hours: JSON.stringify(settings.opening_hours)
      };

      await upsertBusinessSettingsForTenant(tenantId, dataToSave);
      setSuccess('Business settings saved successfully');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings({ ...settings, [name]: value });
    if (error) setError('');
  };

  const handleOpeningHourChange = (day, field, value) => {
    setSettings({
      ...settings,
      opening_hours: {
        ...settings.opening_hours,
        [day]: {
          ...settings.opening_hours[day],
          [field]: value
        }
      }
    });
    if (error) setError('');
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-6 w-48"></div>
          <div className="space-y-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-20 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Business Settings</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-200 rounded-md p-4 mb-6"
          >
            <p className="text-green-700">{success}</p>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiSettings} className="w-5 h-5 mr-2" />
              Basic Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiMail} className="w-4 h-4 inline mr-1" />
                  Business Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={settings.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="business@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiPhone} className="w-4 h-4 inline mr-1" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={settings.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <SafeIcon icon={FiMapPin} className="w-4 h-4 inline mr-1" />
                Business Address
              </label>
              <textarea
                name="address"
                value={settings.address}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="123 Business St, City, State 12345"
              />
            </div>
          </div>

          {/* Opening Hours */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiClock} className="w-5 h-5 mr-2" />
              Opening Hours
            </h2>

            <div className="space-y-4">
              {DAYS_OF_WEEK.map(day => (
                <div key={day} className="flex items-center space-x-4">
                  <div className="w-24 text-sm font-medium text-gray-700">
                    {day}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`${day}-open`}
                      checked={settings.opening_hours[day]?.isOpen || false}
                      onChange={(e) => handleOpeningHourChange(day, 'isOpen', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`${day}-open`} className="text-sm text-gray-700">
                      Open
                    </label>
                  </div>

                  {settings.opening_hours[day]?.isOpen && (
                    <div className="flex items-center space-x-2">
                      <input
                        type="time"
                        value={settings.opening_hours[day]?.openTime || '09:00'}
                        onChange={(e) => handleOpeningHourChange(day, 'openTime', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="time"
                        value={settings.opening_hours[day]?.closeTime || '17:00'}
                        onChange={(e) => handleOpeningHourChange(day, 'closeTime', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  {!settings.opening_hours[day]?.isOpen && (
                    <span className="text-sm text-gray-500">Closed</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={isSaving}
              className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>{isSaving ? 'Saving...' : 'Save Settings'}</span>
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default BusinessSettings;